/**
 * Design System Color Palette
 *
 * Inspired by Stripe, Forecareer, and modern SaaS applications.
 * Provides consistent colors for light and dark themes.
 *
 * @see https://stripe.com for color inspiration
 */

// ============================================================================
// Standalone semantic scales — extracted so the semantic key on `baseColors`
// (e.g., `error`) and its common-name alias (e.g., `red`) share the SAME
// object reference. Single source of truth, no hex-literal drift, smaller
// bundle. `as const` typing is preserved because each scale is itself
// `as const` and `baseColors` references them directly.
// ============================================================================

const indigoScale = {
  50: '#EEF2FF',
  100: '#E0E7FF',
  200: '#C7D2FE',
  300: '#A5B4FC',
  400: '#818CF8',
  500: '#6366F1', // Main indigo
  600: '#4F46E5',
  700: '#4338CA',
  800: '#3730A3',
  900: '#312E81',
} as const;

const successScale = {
  50: '#ECFDF5',
  100: '#D1FAE5',
  200: '#A7F3D0',
  300: '#6EE7B7',
  400: '#34D399',
  500: '#10B981',
  600: '#059669',
  700: '#047857',
  800: '#065F46',
  900: '#064E3B',
} as const;

const warningScale = {
  50: '#FFFBEB',
  100: '#FEF3C7',
  200: '#FDE68A',
  300: '#FCD34D',
  400: '#FBBF24',
  500: '#F59E0B',
  600: '#D97706',
  700: '#B45309',
  800: '#92400E',
  900: '#78350F',
} as const;

const errorScale = {
  50: '#FFF1F2',
  100: '#FFE4E6',
  200: '#FECDD3',
  300: '#FDA4AF',
  400: '#FB7185',
  500: '#F43F5E',
  600: '#E11D48',
  700: '#BE123C',
  800: '#9F1239',
  900: '#881337',
} as const;

const infoScale = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
} as const;

// ============================================================================
// Base Colors
// ============================================================================

export const baseColors = {
  // Primary - Stripe-inspired purple-blue
  primary: {
    50: '#F0F0FF',
    100: '#E0E1FF',
    200: '#C7C8FE',
    300: '#A5A6FC',
    400: '#8183F9',
    500: '#635BFF', // Main primary
    600: '#5046E5',
    700: '#4338CA',
    800: '#3730A3',
    900: '#312E81',
  },

  // Secondary - Cyan accent
  secondary: {
    50: '#ECFEFF',
    100: '#CFFAFE',
    200: '#A5F3FC',
    300: '#67E8F9',
    400: '#22D3EE',
    500: '#00D4FF', // Main secondary
    600: '#0891B2',
    700: '#0E7490',
    800: '#155E75',
    900: '#164E63',
  },

  // Teal - Mint accent retuned to the v3 design DNA (Issue #1758 F1).
  // Design declares the dominant mint as `#4ed9a4` (shell.jsx lines 4-32);
  // the punchier mint replaces the older cyan-leaning teal AND the legacy
  // navigation blue. Shade 400 is the canonical main accent consumed by
  // most components (StatusDot, WorkflowStep, AccentCard.teal, theme glows).
  teal: {
    50: '#EDFBF5',
    100: '#D2F5E5',
    200: '#A8EBCF',
    300: '#7FDBB6', // matches design `mintSoft`
    400: '#4ED9A4', // main mint accent — matches design `mint`
    500: '#2EC189',
    600: '#1FA374',
    700: '#168663',
    800: '#125A45',
    900: '#0E4536',
  },

  // Indigo - Blue/violet accent (for twilight theme); aliased as `violet`
  indigo: indigoScale,

  // Success - Emerald; aliased as `green`
  success: successScale,

  // Warning - Amber; aliased as `amber`
  warning: warningScale,

  // Error - Rose; aliased as `red`
  error: errorScale,

  // Info - Blue; aliased as `blue`
  info: infoScale,

  // Neutral - Slate (for text and backgrounds)
  neutral: {
    0: '#FFFFFF',
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
    950: '#020617',
  },

  // Purple — F.purple semantic (`#a78bfa`) from the v3 design DNA
  // (shell.jsx line 35). Reserved for: LLC entity badges, partner-type
  // chips, "PAID" commission direction, e-sign type, inbound. NEVER for
  // identifiers (IDs render plain text + mono per round-7 rule).
  // Shade 400 is the canonical hue consumed by `palette.secondary.main`
  // in dark mode and any AccentCard.purple uses.
  purple: {
    50: '#F3F0FE',
    100: '#E5DDFD',
    200: '#CDBFFB',
    300: '#BCA5F9',
    400: '#A78BFA', // main purple accent — matches design `F.purple`
    500: '#8B6FF6',
    600: '#7355E3',
    700: '#5A41C4',
    800: '#4632A0',
    900: '#332578',
  },

  // Orange — F.orange semantic (`#e89968`) from the v3 design DNA
  // (shell.jsx line 33). Reserved for: borrower category chips.
  // Shade 400 is the canonical hue; the rest of the scale is interpolated
  // for hover / active variants and for future borrower-tier surfaces.
  orange: {
    50: '#FDF3EC',
    100: '#FBE2D1',
    200: '#F6C4A3',
    300: '#F1AB85',
    400: '#E89968', // main orange accent — matches design `F.orange`
    500: '#DE7B41',
    600: '#C56128',
    700: '#9F4E1F',
    800: '#793C18',
    900: '#532811',
  },

  // ----------------------------------------------------------------
  // Common-name aliases — each is the SAME object reference as its
  // semantic counterpart above, so updates to one propagate to both.
  // Defined as distinct keys so consumers can write either
  // `baseColors.error[500]` or `baseColors.red[500]`.
  // ----------------------------------------------------------------
  red: errorScale,
  amber: warningScale,
  green: successScale,
  // TODO(#1758 F2): consumers using blue/info as a nav/selection accent should migrate to teal[400]; sweep pending.
  blue: infoScale,
  violet: indigoScale,
} as const;

// ============================================================================
// Portal Brand Colors
// ============================================================================

/**
 * Portal-specific brand color scales for differentiation between portals.
 *
 * DESIGN DECISION: Each portal has a unique brand color to help users
 * visually distinguish which portal they're using:
 * - Admin Portal: Gold/amber - conveys authority and premium status
 * - Business Portal: Blue - professional and trustworthy
 * - Consumer Portal: Primary purple (uses baseColors.primary)
 * - Auth Portal: Neutral (uses baseColors.neutral)
 */
export const portalBrandColors = {
  // Admin Portal - Gold/Amber theme for brand differentiation
  adminGold: {
    50: '#FDF8E8',
    100: '#FAF0C8',
    200: '#F5E08F',
    300: '#EFCF56',
    400: '#E8BD2D',
    500: '#D4A017', // Main admin gold
    600: '#B8860B',
    700: '#9A7209',
    800: '#7D5C07',
    900: '#5C4305',
  },

  // Business Portal - Professional blue theme
  /** @deprecated v3 design uses primary.main (mint) instead — remove when callers migrate */
  businessBlue: {
    50: '#E6F2FF',
    100: '#CCE4FF',
    200: '#99C9FF',
    300: '#66AEFF',
    400: '#3393FF',
    500: '#0078D7', // Main business blue
    600: '#0066B8',
    700: '#005499',
    800: '#00427A',
    900: '#00315B',
  },

  // Light blue variants used for backgrounds and highlights
  lightBlue: {
    50: '#E6F4FF',
    100: '#CCE9FF',
    200: '#99D3FF',
    300: '#66BDFF',
    400: '#4CA6FF',
  },

  // Accent yellow for highlights and badges
  accentYellow: {
    light: '#FEF9E7',
    lighter: '#FCF3CF',
    medium: '#F9E79F',
    main: '#F4D35E',
  },
} as const;

// ============================================================================
// Gradient Definitions
// ============================================================================

export const gradients = {
  // Hero/accent gradients
  chroma: 'linear-gradient(135deg, #635BFF 0%, #00D4FF 50%, #A855F7 100%)',
  chromaSubtle: 'linear-gradient(135deg, rgba(99,91,255,0.1) 0%, rgba(0,212,255,0.1) 50%, rgba(168,85,247,0.1) 100%)',

  // Background gradients
  twilight: 'linear-gradient(180deg, #0A2540 0%, #1A1F36 100%)',
  twilightLight: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)',

  // Glow gradients (for backgrounds)
  glowPrimary: 'radial-gradient(ellipse at center, rgba(99,91,255,0.15) 0%, transparent 70%)',
  glowSecondary: 'radial-gradient(ellipse at center, rgba(0,212,255,0.15) 0%, transparent 70%)',
  glowMixed:
    'radial-gradient(ellipse at 30% 20%, rgba(99,91,255,0.12) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(0,212,255,0.12) 0%, transparent 50%)',

  // Card/surface gradients
  glassLight: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
  glassDark: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)',

  // Button gradients
  buttonPrimary: 'linear-gradient(135deg, #635BFF 0%, #7C3AED 100%)',
  buttonSecondary: 'linear-gradient(135deg, #00D4FF 0%, #0EA5E9 100%)',

  // AI/Futuristic gradients
  neonPulse: 'linear-gradient(135deg, #00F5FF 0%, #7C3AED 50%, #FF006E 100%)',
  holographic:
    'linear-gradient(135deg, #FF006E 0%, #FF9A00 17%, #FFE600 33%, #00F5A0 50%, #00D4FF 67%, #7C3AED 83%, #FF006E 100%)',
  mesh: 'radial-gradient(ellipse at 20% 50%, rgba(99,91,255,0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(0,212,255,0.3) 0%, transparent 50%), radial-gradient(ellipse at 50% 80%, rgba(168,85,247,0.3) 0%, transparent 50%)',
  aurora: 'linear-gradient(135deg, #00F5A0 0%, #00D4FF 40%, #7C3AED 100%)',
  darkRadial:
    'radial-gradient(ellipse at center, rgba(15,15,25,0.4) 0%, rgba(5,5,15,0.95) 70%), radial-gradient(ellipse at 20% 80%, rgba(99,91,255,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(0,212,255,0.08) 0%, transparent 50%)',
} as const;

// ============================================================================
// Light Theme Palette
// ============================================================================

export const lightPalette = {
  mode: 'light' as const,

  primary: {
    main: baseColors.primary[500],
    light: baseColors.primary[400],
    dark: baseColors.primary[700],
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: baseColors.secondary[500],
    light: baseColors.secondary[400],
    dark: baseColors.secondary[700],
    contrastText: '#FFFFFF',
  },

  success: {
    main: baseColors.success[500],
    light: baseColors.success[400],
    dark: baseColors.success[700],
    contrastText: '#FFFFFF',
  },

  warning: {
    main: baseColors.warning[500],
    light: baseColors.warning[400],
    dark: baseColors.warning[700],
    contrastText: '#000000',
  },

  error: {
    main: baseColors.error[500],
    light: baseColors.error[400],
    dark: baseColors.error[700],
    contrastText: '#FFFFFF',
  },

  info: {
    main: baseColors.info[500],
    light: baseColors.info[400],
    dark: baseColors.info[700],
    contrastText: '#FFFFFF',
  },

  background: {
    default: baseColors.neutral[50],
    paper: baseColors.neutral[0],
    subtle: baseColors.neutral[100],
  },

  text: {
    primary: baseColors.neutral[900],
    secondary: baseColors.neutral[600],
    disabled: baseColors.neutral[400],
  },

  divider: baseColors.neutral[200],

  action: {
    active: baseColors.neutral[600],
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(99, 91, 255, 0.08)',
    disabled: baseColors.neutral[300],
    disabledBackground: baseColors.neutral[100],
  },
};

// ============================================================================
// Dark Theme Palette
// ============================================================================

// Dark palette aligned 1:1 with the v3 design source-of-truth `F.*` tokens
// (`/tmp/design-fetch-new/fiops/project/shell.jsx` lines 4-37). Each MUI key
// below maps to one design token so component code can use
// `theme.palette.<key>` directly and trust the value.
//
// Primary = `F.mint` (#4ED9A4 — `baseColors.teal[400]`). Mint is the v3
// platform accent (Astor, active, success, "in flight"). Earlier dark mode
// used `baseColors.primary[400]` purple-blue — replaced because the design
// reserves purple for entity-type semantics only (LLC, partner-type, "PAID"
// commission). See spec `00-tokens-and-invariants.md` for the full mapping.
//
// Secondary = `F.purple` (#A78BFA — `baseColors.purple[400]`). Used for
// LLC badges, partner-type chips, e-sign tags, "PAID" direction. Replaces
// the cyan secondary (`baseColors.secondary[400]`) which was unused by the
// v3 surfaces.
//
// `F.id` MUST equal `text.primary` (mono font alone gives the distinction —
// per round-7 rule). No separate `id` token is defined on the palette.
// `F.blue` is a LEGACY alias → `F.mint` in the design source; consumers
// keep using `baseColors.info` as a base scale where blue is a distinct
// semantic (partner-type chips), but no theme-level alias is needed here.
export const darkPalette = {
  mode: 'dark' as const,

  primary: {
    main: baseColors.teal[400], // #4ED9A4 — F.mint
    light: baseColors.teal[300], // #7FDBB6 — F.mintSoft
    dark: baseColors.teal[600],
    contrastText: '#000000',
  },

  secondary: {
    main: baseColors.purple[400], // #A78BFA — F.purple
    light: baseColors.purple[300],
    dark: baseColors.purple[600],
    contrastText: '#000000',
  },

  // F.mint also signals success/healthy in the v3 chip vocab — kept on a
  // mint-family hex so `theme.palette.success.main` resolves close to mint
  // without colliding with `palette.primary.main`.
  success: {
    main: '#6ed498',
    light: '#9fe5ba',
    dark: baseColors.success[600],
    contrastText: '#000000',
  },

  warning: {
    main: '#e8b765', // F.amber
    light: '#f1d196',
    dark: baseColors.warning[700],
    contrastText: '#000000',
  },

  error: {
    main: '#e57373', // F.red
    light: '#f0a4a4',
    dark: baseColors.error[700],
    contrastText: '#FFFFFF',
  },

  info: {
    main: baseColors.info[400],
    light: baseColors.info[300],
    dark: baseColors.info[600],
    contrastText: '#000000',
  },

  // Surface palette aligned to the v3 design source-of-truth (shell.jsx
  // F.bg / F.panel / F.panel2 / F.panel3). Cool near-black canvas, layered
  // paper / subtle surfaces with 4-6pt steps so 1px hairline dividers stay
  // visible.
  //
  // `subtle` and `elevated` both resolve to F.panel2 (#131a23). Spec calls
  // it `background.elevated`; the older `background.subtle` key is kept as
  // an alias so existing call-sites don't break — both pointers point at the
  // same value so future drift is impossible.
  background: {
    default: '#0a0e13', // F.bg
    paper: '#0e141b', // F.panel
    subtle: '#131a23', // F.panel2 (legacy name)
    elevated: '#131a23', // F.panel2 (spec name — same value as subtle)
  },

  text: {
    primary: '#e6eaf0', // F.text — also F.id (mono font gives the distinction)
    secondary: '#8a95a5', // F.textDim
    disabled: '#5a6573', // F.textMute
  },

  divider: '#1c2530', // F.border — solid hex per design (1px hairline)

  action: {
    active: '#8a95a5', // F.textDim
    hover: '#192230', // F.panel3 — tertiary inset (kbd, count pill, slider track)
    selected: 'rgba(78, 217, 164, 0.16)', // F.mintDim-adjacent (matches primary.main rgba)
    disabled: '#5a6573', // F.textMute
    disabledBackground: 'rgba(255, 255, 255, 0.06)', // F.borderSoft
  },
};

// ============================================================================
// Midnight Theme Palette (NEW - Near-Black Aesthetic)
// ============================================================================

export const midnightPalette = {
  mode: 'dark' as const,

  primary: {
    main: baseColors.primary[400],
    light: baseColors.primary[300],
    dark: baseColors.primary[600],
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: baseColors.secondary[400],
    light: baseColors.secondary[300],
    dark: baseColors.secondary[600],
    contrastText: '#000000',
  },

  success: {
    main: baseColors.success[400],
    light: baseColors.success[300],
    dark: baseColors.success[600],
    contrastText: '#000000',
  },

  warning: {
    main: baseColors.warning[400],
    light: baseColors.warning[300],
    dark: baseColors.warning[600],
    contrastText: '#000000',
  },

  error: {
    main: baseColors.error[400],
    light: baseColors.error[300],
    dark: baseColors.error[600],
    contrastText: '#FFFFFF',
  },

  info: {
    main: baseColors.info[400],
    light: baseColors.info[300],
    dark: baseColors.info[600],
    contrastText: '#000000',
  },

  // Near-black backgrounds - the key differentiator from dark theme
  background: {
    default: '#0a0a0a', // Near black (main background)
    paper: '#141414', // Slightly lighter (cards, surfaces)
    subtle: '#1a1a1a', // Card backgrounds, nested surfaces
    elevated: '#1f1f1f', // Elevated surfaces (modals, dropdowns)
  },

  text: {
    primary: 'rgba(255, 255, 255, 0.95)',
    secondary: 'rgba(148, 163, 184, 0.9)', // Slightly muted slate
    disabled: 'rgba(255, 255, 255, 0.4)',
  },

  divider: 'rgba(255, 255, 255, 0.08)',

  action: {
    active: 'rgba(255, 255, 255, 0.7)',
    hover: 'rgba(255, 255, 255, 0.05)',
    selected: 'rgba(99, 91, 255, 0.16)',
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.08)',
  },
};

// ============================================================================
// Twilight Theme Palette (NEW - Blue-Tinted Dark Aesthetic)
// ============================================================================

export const twilightPalette = {
  mode: 'dark' as const,

  // Use indigo as primary for twilight theme
  primary: {
    main: baseColors.indigo[500],
    light: baseColors.indigo[400],
    dark: baseColors.indigo[600],
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: baseColors.secondary[400],
    light: baseColors.secondary[300],
    dark: baseColors.secondary[600],
    contrastText: '#000000',
  },

  success: {
    main: baseColors.success[400],
    light: baseColors.success[300],
    dark: baseColors.success[600],
    contrastText: '#000000',
  },

  warning: {
    main: baseColors.warning[400],
    light: baseColors.warning[300],
    dark: baseColors.warning[600],
    contrastText: '#000000',
  },

  error: {
    main: baseColors.error[400],
    light: baseColors.error[300],
    dark: baseColors.error[600],
    contrastText: '#FFFFFF',
  },

  info: {
    main: baseColors.info[400],
    light: baseColors.info[300],
    dark: baseColors.info[600],
    contrastText: '#000000',
  },

  // Blue-tinted dark backgrounds - the key differentiator
  background: {
    default: '#0a1019', // Dark navy (main background)
    paper: '#0f1621', // Card backgrounds
    subtle: '#141d2b', // Nested surfaces
    elevated: '#1a2537', // Modals, dropdowns
  },

  text: {
    primary: 'rgba(255, 255, 255, 0.95)',
    secondary: 'rgba(148, 163, 184, 0.85)', // Slate-tinted gray
    disabled: 'rgba(255, 255, 255, 0.4)',
  },

  divider: 'rgba(148, 163, 184, 0.12)',

  action: {
    active: 'rgba(255, 255, 255, 0.7)',
    hover: 'rgba(148, 163, 184, 0.08)',
    selected: 'rgba(99, 102, 241, 0.16)', // Indigo-based selection
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.08)',
  },
};

// ============================================================================
// Accent Color Definitions (for AccentCard borders)
// ============================================================================

export const accentColors = {
  // v3 mint primary — teal[400] = #4ED9A4 matches design DNA `F.mint`. The
  // previous teal[500] = #2EC189 was a darker step that didn't match the spec.
  teal: baseColors.teal[400],
  cyan: baseColors.secondary[500],
  purple: baseColors.primary[500],
  green: baseColors.success[500],
  amber: baseColors.warning[500],
  rose: baseColors.error[500],
  blue: baseColors.info[500],
} as const;

export type AccentColor = keyof typeof accentColors;

// ============================================================================
// Semantic Status Colors (for forms, steps, badges)
// ============================================================================

/**
 * Status colors for form steps, progress indicators, and validation states.
 * Use these instead of hardcoding hex values.
 */
export const statusColors = {
  /** Completed/success state - green */
  completed: baseColors.success[500],
  /** Pending/waiting state - amber */
  pending: baseColors.warning[500],
  /** Error/failed state - red */
  error: baseColors.error[500],
  /** Active/current state - green (same as completed for progress) */
  active: baseColors.success[500],
  /** Inactive/disabled state - gray */
  inactive: baseColors.neutral[400],
  /** In progress state - blue */
  inProgress: baseColors.info[500],
} as const;

export type StatusColor = keyof typeof statusColors;

/**
 * Severity colors for audit logs, alerts, and notifications.
 * Use these for severity indicators in logs and monitoring.
 */
export const severityColors = {
  /** Critical severity - dark red */
  critical: baseColors.error[600],
  /** Error severity - red */
  error: baseColors.error[500],
  /** Warning severity - amber */
  warning: baseColors.warning[500],
  /** Info severity - blue */
  info: baseColors.info[500],
  /** Debug/trace severity - gray */
  debug: baseColors.neutral[500],
} as const;

export type SeverityColor = keyof typeof severityColors;

/**
 * Event type colors for timeline and activity logs.
 * Use these for different types of events in timelines.
 */
export const eventTypeColors = {
  /** Created event - green */
  created: baseColors.success[500],
  /** Updated event - blue */
  updated: baseColors.info[500],
  /** Deleted event - red */
  deleted: baseColors.error[500],
  /** Comment event - purple */
  comment: baseColors.primary[400],
  /** Status change event - amber */
  statusChange: baseColors.warning[500],
  /** Assignment event - cyan */
  assignment: baseColors.secondary[500],
  /** Custom/default event - gray */
  custom: baseColors.neutral[500],
} as const;

export type EventTypeColor = keyof typeof eventTypeColors;

/**
 * Get event type color with dark mode support
 */
export function getEventTypeColor(type: EventTypeColor, isDark: boolean): string {
  // Custom event uses different shades for light/dark
  if (type === 'custom') {
    return isDark ? baseColors.neutral[400] : baseColors.neutral[500];
  }
  return eventTypeColors[type];
}

// ============================================================================
// Chart Colors (theme-aware colors for data visualization)
// ============================================================================

/**
 * Theme-aware chart color palettes for Recharts, Chart.js, etc.
 * Use getChartColors(theme) to get the appropriate palette.
 */
export const chartColors = {
  /** Light theme chart colors - vibrant for visibility on white */
  light: {
    primary: '#635BFF', // Purple (primary brand)
    secondary: '#00D4FF', // Cyan
    success: '#10B981', // Green
    warning: '#F59E0B', // Amber
    error: '#F43F5E', // Rose
    info: '#3B82F6', // Blue
    // Extended palette for multi-series charts
    series: [
      '#635BFF', // Purple
      '#10B981', // Green
      '#F59E0B', // Amber
      '#F43F5E', // Rose
      '#3B82F6', // Blue
      '#8B5CF6', // Violet
      '#EC4899', // Pink
      '#06B6D4', // Cyan
    ],
    // Gradient stops for area charts
    gradients: {
      primary: { start: '#635BFF', end: 'rgba(99, 91, 255, 0)' },
      success: { start: '#10B981', end: 'rgba(16, 185, 129, 0)' },
      warning: { start: '#F59E0B', end: 'rgba(245, 158, 11, 0)' },
      error: { start: '#F43F5E', end: 'rgba(244, 63, 94, 0)' },
    },
    // Text and grid colors
    text: '#64748B',
    grid: '#E2E8F0',
    tooltip: {
      background: '#FFFFFF',
      border: '#E2E8F0',
      text: '#1E293B',
    },
  },
  /** Dark theme chart colors - slightly brighter for dark backgrounds */
  dark: {
    primary: '#8183F9', // Lighter purple
    secondary: '#22D3EE', // Lighter cyan
    success: '#34D399', // Lighter green
    warning: '#FBBF24', // Lighter amber
    error: '#FB7185', // Lighter rose
    info: '#60A5FA', // Lighter blue
    series: [
      '#8183F9', // Purple
      '#34D399', // Green
      '#FBBF24', // Amber
      '#FB7185', // Rose
      '#60A5FA', // Blue
      '#A78BFA', // Violet
      '#F472B6', // Pink
      '#22D3EE', // Cyan
    ],
    gradients: {
      primary: { start: '#8183F9', end: 'rgba(129, 131, 249, 0)' },
      success: { start: '#34D399', end: 'rgba(52, 211, 153, 0)' },
      warning: { start: '#FBBF24', end: 'rgba(251, 191, 36, 0)' },
      error: { start: '#FB7185', end: 'rgba(251, 113, 133, 0)' },
    },
    text: 'rgba(255, 255, 255, 0.7)',
    grid: 'rgba(255, 255, 255, 0.1)',
    tooltip: {
      background: '#1E293B',
      border: 'rgba(255, 255, 255, 0.1)',
      text: '#F8FAFC',
    },
  },
  /** Twilight theme chart colors - indigo-accented for blue backgrounds */
  twilight: {
    primary: '#818CF8', // Indigo (primary for twilight)
    secondary: '#22D3EE', // Cyan
    success: '#34D399', // Green
    warning: '#FBBF24', // Amber
    error: '#FB7185', // Rose
    info: '#60A5FA', // Blue
    series: [
      '#818CF8', // Indigo
      '#34D399', // Green
      '#FBBF24', // Amber
      '#FB7185', // Rose
      '#60A5FA', // Blue
      '#A78BFA', // Violet
      '#F472B6', // Pink
      '#22D3EE', // Cyan
    ],
    gradients: {
      primary: { start: '#818CF8', end: 'rgba(129, 140, 248, 0)' },
      success: { start: '#34D399', end: 'rgba(52, 211, 153, 0)' },
      warning: { start: '#FBBF24', end: 'rgba(251, 191, 36, 0)' },
      error: { start: '#FB7185', end: 'rgba(251, 113, 133, 0)' },
    },
    text: 'rgba(148, 163, 184, 0.85)',
    grid: 'rgba(148, 163, 184, 0.12)',
    tooltip: {
      background: '#0f1621',
      border: 'rgba(148, 163, 184, 0.2)',
      text: '#F8FAFC',
    },
  },
  /** Midnight theme chart colors - high contrast for near-black */
  midnight: {
    primary: '#A78BFA', // Violet (more visible on black)
    secondary: '#22D3EE', // Cyan
    success: '#4ADE80', // Brighter green
    warning: '#FCD34D', // Brighter amber
    error: '#FDA4AF', // Lighter rose
    info: '#93C5FD', // Lighter blue
    series: [
      '#A78BFA', // Violet
      '#4ADE80', // Green
      '#FCD34D', // Amber
      '#FDA4AF', // Rose
      '#93C5FD', // Blue
      '#C4B5FD', // Light violet
      '#FBCFE8', // Light pink
      '#67E8F9', // Light cyan
    ],
    gradients: {
      primary: { start: '#A78BFA', end: 'rgba(167, 139, 250, 0)' },
      success: { start: '#4ADE80', end: 'rgba(74, 222, 128, 0)' },
      warning: { start: '#FCD34D', end: 'rgba(252, 211, 77, 0)' },
      error: { start: '#FDA4AF', end: 'rgba(253, 164, 175, 0)' },
    },
    text: 'rgba(255, 255, 255, 0.8)',
    grid: 'rgba(255, 255, 255, 0.08)',
    tooltip: {
      background: '#141414',
      border: 'rgba(255, 255, 255, 0.1)',
      text: '#F8FAFC',
    },
  },
};

export interface ChartColorPalette {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  series: string[];
  gradients: {
    primary: { start: string; end: string };
    success: { start: string; end: string };
    warning: { start: string; end: string };
    error: { start: string; end: string };
  };
  text: string;
  grid: string;
  tooltip: {
    background: string;
    border: string;
    text: string;
  };
}

export type ChartThemeMode = keyof typeof chartColors;

/**
 * Get chart colors for a specific theme mode
 */
export function getChartColors(mode: ChartThemeMode): ChartColorPalette {
  return chartColors[mode] || chartColors.light;
}

// ============================================================================
// Provider Brand Colors (intentionally hardcoded - brand identity)
// ============================================================================

/**
 * Brand colors for AI model providers.
 * These are intentionally NOT theme-dependent - they represent brand identity.
 */
export const providerBrandColors = {
  openai: '#10a37f',
  anthropic: '#d97757',
  google: '#4285f4',
  cohere: '#39dbb1',
  meta: '#0084ff',
  bedrock: '#ff9900',
  vertex: '#4285f4',
  mistral: '#f7d046',
  azure: '#0078d4',
  default: '#64748B',
} as const;

export type ProviderBrand = keyof typeof providerBrandColors;

/**
 * Get provider brand color
 */
export function getProviderColor(provider: string): string {
  const normalizedProvider = provider.toLowerCase() as ProviderBrand;
  return providerBrandColors[normalizedProvider] || providerBrandColors.default;
}

// ============================================================================
// Neon Accent Colors (for NeonCard glow effects)
// ============================================================================

/**
 * Neon accent color definitions with background/border/glow variants.
 * Each accent provides a cohesive color scheme where:
 * - bg: Subtle background tint (0.08 opacity)
 * - bgLight: Light theme background (0.12 opacity)
 * - border: Card border color (0.3 opacity)
 * - borderLight: Light theme border (0.4 opacity)
 * - glow: Glow effect color (0.5 opacity)
 */
/**
 * Neon accent color definitions with semantic and raw color variants.
 * Each accent provides a cohesive color scheme where:
 * - bg: Transparent (no colored background fill)
 * - bgLight: Transparent (no colored background fill)
 * - border: Card border color (0.3 opacity)
 * - borderLight: Light theme border (0.4 opacity)
 * - glow: Glow effect color (0.5 opacity)
 */
export const neonAccents = {
  // Neutral - subtle gray for informational cards without semantic meaning
  neutral: {
    base: baseColors.neutral[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(100, 116, 139, 0.2)',
    borderLight: 'rgba(100, 116, 139, 0.18)',
    glow: 'rgba(100, 116, 139, 0.3)',
  },
  // Primary - matches theme primary color (Stripe purple-blue)
  primary: {
    base: baseColors.primary[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(99, 91, 255, 0.3)',
    borderLight: 'rgba(99, 91, 255, 0.2)',
    glow: 'rgba(99, 91, 255, 0.5)',
  },
  // Success - green for positive states
  success: {
    base: baseColors.success[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(16, 185, 129, 0.3)',
    borderLight: 'rgba(16, 185, 129, 0.2)',
    glow: 'rgba(16, 185, 129, 0.5)',
  },
  // Warning - amber for caution states
  warning: {
    base: baseColors.warning[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(245, 158, 11, 0.3)',
    borderLight: 'rgba(245, 158, 11, 0.2)',
    glow: 'rgba(245, 158, 11, 0.5)',
  },
  // Error - red for error states
  error: {
    base: baseColors.error[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(239, 68, 68, 0.3)',
    borderLight: 'rgba(239, 68, 68, 0.2)',
    glow: 'rgba(239, 68, 68, 0.5)',
  },
  // Info - cyan for informational highlights
  info: {
    base: baseColors.info[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(14, 165, 233, 0.3)',
    borderLight: 'rgba(14, 165, 233, 0.2)',
    glow: 'rgba(14, 165, 233, 0.5)',
  },
  // Secondary - purple accent
  secondary: {
    base: '#8b5cf6',
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(139, 92, 246, 0.3)',
    borderLight: 'rgba(139, 92, 246, 0.2)',
    glow: 'rgba(139, 92, 246, 0.5)',
  },
  // Pink - decorative accent
  pink: {
    base: '#ec4899',
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(236, 72, 153, 0.3)',
    borderLight: 'rgba(236, 72, 153, 0.2)',
    glow: 'rgba(236, 72, 153, 0.5)',
  },
  // Legacy aliases (deprecated - use semantic names above)
  cyan: {
    base: baseColors.info[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(14, 165, 233, 0.3)',
    borderLight: 'rgba(14, 165, 233, 0.2)',
    glow: 'rgba(14, 165, 233, 0.5)',
  },
  purple: {
    base: '#8b5cf6',
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(139, 92, 246, 0.3)',
    borderLight: 'rgba(139, 92, 246, 0.2)',
    glow: 'rgba(139, 92, 246, 0.5)',
  },
  green: {
    base: baseColors.success[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(16, 185, 129, 0.3)',
    borderLight: 'rgba(16, 185, 129, 0.2)',
    glow: 'rgba(16, 185, 129, 0.5)',
  },
  amber: {
    base: baseColors.warning[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(245, 158, 11, 0.3)',
    borderLight: 'rgba(245, 158, 11, 0.2)',
    glow: 'rgba(245, 158, 11, 0.5)',
  },
  red: {
    base: baseColors.error[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(239, 68, 68, 0.3)',
    borderLight: 'rgba(239, 68, 68, 0.2)',
    glow: 'rgba(239, 68, 68, 0.5)',
  },
  // Portal brand accents
  adminGold: {
    base: portalBrandColors.adminGold[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(212, 160, 23, 0.3)',
    borderLight: 'rgba(212, 160, 23, 0.2)',
    glow: 'rgba(212, 160, 23, 0.5)',
  },
  /** @deprecated v3 design uses primary.main (mint) instead — remove when callers migrate */
  businessBlue: {
    base: portalBrandColors.businessBlue[500],
    bg: 'transparent',
    bgLight: 'transparent',
    border: 'rgba(0, 120, 215, 0.3)',
    borderLight: 'rgba(0, 120, 215, 0.2)',
    glow: 'rgba(0, 120, 215, 0.5)',
  },
} as const;

/**
 * Semantic neon accent keys for type-safe usage.
 * Use these constants instead of string literals.
 */
export const NeonAccentKey = {
  /** Neutral - for informational cards without semantic meaning */
  NEUTRAL: 'neutral',
  /** Primary - brand color accent */
  PRIMARY: 'primary',
  /** Success - positive state (e.g., completed, approved) */
  SUCCESS: 'success',
  /** Warning - caution state (e.g., pending action, expiring) */
  WARNING: 'warning',
  /** Error - error state (e.g., failed, rejected) */
  ERROR: 'error',
  /** Info - informational highlight */
  INFO: 'info',
  /** Secondary - secondary accent color */
  SECONDARY: 'secondary',
  /** Pink - decorative accent */
  PINK: 'pink',
} as const;

export type NeonAccent = keyof typeof neonAccents;

/**
 * Get neon styles for a given accent color, adjusted for theme mode
 */
export function getNeonStyles(accent: NeonAccent, isDark: boolean) {
  const colors = neonAccents[accent];
  return {
    background: isDark ? colors.bg : colors.bgLight,
    border: isDark ? colors.border : colors.borderLight,
    base: colors.base,
    glow: colors.glow,
  };
}
